<script setup>
import { ref, computed, watch } from "vue";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { DatePicker } from "@/shared/ui/date-picker";

const props = defineProps({
  label: { type: String, required: true },
  modelValue: { type: [String, Number, null], default: null },
  type: { type: String, default: "text" }, // text | date | textarea | number
  options: { type: Array, default: null },
  full: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: "" },
  disableFuture: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "focus"]);

const local = ref(props.modelValue ?? "");

watch(
  () => props.modelValue,
  (v) => {
    local.value = v ?? "";
  }
);

function emitImmediate(v) {
  local.value = v ?? "";
  emit("update:modelValue", v === "" || v == null ? null : v);
}

const normalizedOptions = computed(() =>
  (props.options || []).map((o) =>
    typeof o === "object" && o !== null
      ? { value: o.value, label: o.label ?? String(o.value) }
      : { value: o, label: String(o) }
  )
);
</script>

<template>
  <div :class="['flex flex-col  min-w-0', full && 'col-span-full']">
    <span
      class="text-[12.5px] font-medium text-muted-foreground flex items-center gap-1"
    >
      {{ label }}
      <span v-if="required" class="text-destructive">*</span>
    </span>

    <Select
      v-if="options"
      :model-value="local || undefined"
      :disabled="disabled"
      @update:model-value="emitImmediate"
      size="sm"
    >
      <SelectTrigger class="w-full">
        <SelectValue :placeholder="placeholder || $t('не выбрано')" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem
          v-for="o in normalizedOptions"
          :key="o.value"
          :value="o.value"
        >
          {{ o.label }}
        </SelectItem>
      </SelectContent>
    </Select>

    <DatePicker
      v-else-if="type === 'date'"
      :model-value="local || ''"
      :placeholder="placeholder || $t('Выберите дату')"
      :disable-future="disableFuture"
      @update:model-value="emitImmediate"
      size="sm"
    />

    <Textarea
      v-else-if="type === 'textarea'"
      :model-value="modelValue ?? ''"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="(e) => emitImmediate(e.target.value)"
      size="sm"
    />

    <Input
      v-else
      :model-value="modelValue ?? ''"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      size="sm"
      @input="(e) => emitImmediate(e.target.value)"
      @focus="(e) => emit('focus', e)"
    />
  </div>
</template>
